import React, { useEffect, useRef, useState, useCallback } from "react";
import api, { formatTL, formatDateTime, formatDuration } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plate } from "@/components/Plate";
import PayMethodDialog from "@/components/PayMethodDialog";
import { X, CarProfile, SignIn, SignOut, Money, Gift, User } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Global vehicle activity popup that appears on every admin/operator page.
 * - Polls /api/sessions/live-activity every 5 seconds
 * - Opens a persistent popup for each new entry/exit
 * - The popup stays open until the staff manually closes it OR
 *   until the session's payment is marked odendi/muaf
 * - Multiple simultaneous activities queue up as a stack of cards
 */
const POLL_INTERVAL_MS = 5000;
const WINDOW_SECONDS = 60;
const DISMISSED_KEY = "otopark_dismissed_activities";

function loadDismissed() {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
}

function saveDismissed(set) {
  try {
    // Keep only last 200 to avoid bloat
    const arr = Array.from(set).slice(-200);
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(arr));
  } catch { /* ignore */ }
}

function keyFor(item) {
  // Unique key per event: session_id + event_kind + event_at (session may have both entry & exit)
  return `${item.id}::${item._event_kind}::${item._event_at || ""}`;
}

export default function GlobalVehicleActivityMonitor() {
  const [items, setItems] = useState([]);           // active popup queue
  const [payFor, setPayFor] = useState(null);       // session opened in PayMethodDialog
  const dismissedRef = useRef(loadDismissed());

  const dismiss = useCallback((k) => {
    dismissedRef.current.add(k);
    saveDismissed(dismissedRef.current);
    setItems((cur) => cur.filter((it) => keyFor(it) !== k));
  }, []);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get(`/sessions/live-activity?seconds=${WINDOW_SECONDS}`);
      const list = Array.isArray(data) ? data : [];
      const newItems = [];
      for (const it of list) {
        const k = keyFor(it);
        if (dismissedRef.current.has(k)) continue;
        // Auto-dismiss when session becomes odendi/muaf (paid or waived)
        if (it._event_kind === "exit"
            && (it.payment_status === "odendi" || it.payment_status === "muaf")) {
          dismissedRef.current.add(k);
          saveDismissed(dismissedRef.current);
          continue;
        }
        newItems.push(it);
      }
      // Sort newest first
      newItems.sort((a, b) => (b._event_at || "").localeCompare(a._event_at || ""));
      setItems(newItems);
    } catch (_e) {
      // Silent — poll continues
    }
  }, []);

  useEffect(() => {
    poll();
    const iv = setInterval(poll, POLL_INTERVAL_MS);
    // Also poll on tab focus
    const onFocus = () => poll();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
    };
  }, [poll]);

  const collectPayment = (it) => {
    // Ödemeyi doğrudan bu popup'tan al — sayfa değiştirme yok
    setPayFor(it);
  };

  const onPaymentDone = () => {
    if (payFor) {
      dismiss(keyFor(payFor));
    }
    setPayFor(null);
    toast.success("Ödeme alındı — Kasa ve Tahsilatlar güncellendi");
  };

  const waivePayment = async (it) => {
    if (!window.confirm(`${it.plate} misafir muafiyeti uygulansın mı? Ücret ₺${(it.fee || 0).toFixed(2)} sıfırlanacak.`)) return;
    try {
      await api.post(`/sessions/${it.id}/waive`, { reason: "Global popup misafir muafiyeti" });
      toast.success(`${it.plate} muaf edildi`);
      dismiss(keyFor(it));
    } catch (e) { toast.error(e?.response?.data?.detail || "Muaf edilemedi"); }
  };

  return (
    <>
      {items.length > 0 && (
      <div
        className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center p-4"
        data-testid="global-vehicle-activity"
      >
        {/* Semi-transparent backdrop (click-to-dismiss disabled — kullanıcı ödeme yapana veya X'e basana kadar sabit) */}
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm pointer-events-auto" />
        <div className="relative pointer-events-auto flex flex-col gap-3 max-w-[420px] w-full max-h-[90vh] overflow-y-auto">
          {items.slice(0, 4).map((it) => {
          const isEntry = it._event_kind === "entry";
          const hasFee = !isEntry && (it.fee || 0) > 0 && it.payment_status === "bekliyor";
          const k = keyFor(it);
          const imgUrl = isEntry ? it.entry_image_url : (it.exit_image_url || it.entry_image_url);
          const fullImgUrl = imgUrl
            ? (imgUrl.startsWith("http") ? imgUrl : `${process.env.REACT_APP_BACKEND_URL}${imgUrl}`)
            : null;
          return (
            <div
              key={k}
              className={`rounded-sm border-2 shadow-2xl bg-card animate-slide-in ${
                isEntry ? "border-blue-500" : hasFee ? "border-amber-500" : "border-emerald-500"
              }`}
              data-testid={`activity-card-${it.plate}`}
            >
              <div className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    {isEntry ? (
                      <SignIn size={28} weight="fill" className="text-blue-600" />
                    ) : (
                      <SignOut size={28} weight="fill" className={hasFee ? "text-amber-600" : "text-emerald-600"} />
                    )}
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                        {isEntry ? "Yeni Giriş" : "Yeni Çıkış"} · {it.parking_lot_key === "ust_kat" ? "Üst Kat" : "Alt Kat"}
                      </div>
                      <Plate country={it.country_code}>{it.plate}</Plate>
                      {it.customer_name && (
                        <div className="mt-1 text-xs font-semibold flex items-center gap-1 text-primary"
                             data-testid={`activity-customer-${it.plate}`}
                             title={it.customer_phone || ""}>
                          <User size={11} weight="fill" />
                          {it.customer_name}
                          {it.customer_phone && (
                            <span className="text-muted-foreground font-mono font-normal ml-1">
                              · {it.customer_phone}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => dismiss(k)}
                    data-testid={`activity-close-${it.plate}`}
                    className="h-8 w-8 p-0"
                    title="Kapat"
                  >
                    <X size={18} />
                  </Button>
                </div>

                {/* Vehicle thumbnail — plaka fotoğrafı, operatörün fiziki eşleştirmesi için */}
                {fullImgUrl && (
                  <div className="relative border border-border rounded-sm overflow-hidden bg-muted"
                       data-testid={`activity-image-${it.plate}`}>
                    <img
                      src={fullImgUrl}
                      alt={`${it.plate} ${isEntry ? "girişi" : "çıkışı"}`}
                      className="w-full h-40 object-cover"
                      data-fallback-tried="0"
                      onError={(e) => {
                        const img = e.target;
                        const entryUrl = it.entry_image_url
                          ? (it.entry_image_url.startsWith("http")
                              ? it.entry_image_url
                              : `${process.env.REACT_APP_BACKEND_URL}${it.entry_image_url}`)
                          : null;
                        if (img.dataset.fallbackTried !== "1" && entryUrl && img.src !== entryUrl) {
                          img.dataset.fallbackTried = "1";
                          img.src = entryUrl;
                        } else {
                          // Fotoğraf mevcut değil — gizleme yerine bilgi ver
                          img.parentElement.innerHTML =
                            '<div class="w-full h-40 flex items-center justify-center text-xs text-muted-foreground bg-muted uppercase tracking-widest">Fotoğraf mevcut değil</div>';
                        }
                      }}
                    />
                    <div className="absolute bottom-1 right-1 bg-black/70 text-white text-[9px] px-1.5 py-0.5 uppercase tracking-widest rounded-sm">
                      {isEntry ? "Giriş" : "Çıkış"} Anı
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 text-sm">
                  {isEntry ? (
                    <>
                      <div>
                        <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Giriş</div>
                        <div className="font-mono text-xs">{formatDateTime(it.entry_time)}</div>
                      </div>
                      <div>
                        <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Durum</div>
                        <div className="font-semibold text-blue-600">Aktif · Park Halinde</div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Süre</div>
                        <div className="font-mono">{formatDuration(it.duration_minutes)}</div>
                      </div>
                      <div>
                        <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Tutar</div>
                        <div className={`font-mono font-bold text-lg ${hasFee ? "text-amber-700" : "text-emerald-600"}`}>
                          {formatTL(it.fee)}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {hasFee && (
                  <div className="pt-2 border-t border-border flex gap-2">
                    <Button
                      onClick={() => collectPayment(it)}
                      data-testid={`activity-collect-${it.plate}`}
                      className="flex-1 h-10 rounded-sm bg-amber-600 hover:bg-amber-700 text-white text-xs uppercase tracking-widest font-semibold"
                    >
                      <Money size={14} /> Ödeme Al
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => waivePayment(it)}
                      data-testid={`activity-waive-${it.plate}`}
                      className="flex-1 h-10 rounded-sm text-xs uppercase tracking-widest font-semibold"
                    >
                      <Gift size={14} /> Misafir
                    </Button>
                  </div>
                )}
                {!isEntry && !hasFee && (it.payment_status === "muaf" || it.subscription_used) && (
                  <div className="text-xs text-emerald-700 font-semibold uppercase tracking-widest text-center py-2 border-t border-border">
                    <CarProfile size={14} className="inline mr-1" />
                    Abonelik / Muaf — Ödeme Gerekmiyor
                  </div>
                )}
              </div>
            </div>
          );
        })}
          {items.length > 4 && (
            <div className="text-center text-[11px] uppercase tracking-widest text-muted-foreground bg-card border border-border rounded-sm py-1.5"
                 data-testid="activity-overflow">
              + {items.length - 4} daha (kapatarak sıradakini gör)
            </div>
          )}
        </div>
      </div>
      )}

      {/* Popup içinden ödeme al */}
      <PayMethodDialog
        session={payFor}
        open={!!payFor}
        onOpenChange={(o) => !o && setPayFor(null)}
        onDone={onPaymentDone}
        hideCoupon={false}
      />
    </>
  );
}
